import Toast from 'react-native-toast-message';
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';
import DataExporter from '../utils/DataExporter';
import { PEOPLE_SELECT } from '../utils/peopleSelect';
import { normalizeResourcePaths } from '../utils/formResourcePolicy';

const PUBLIC_ID_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const PUBLIC_ID_LENGTH = 10;

/**
 * Generate a random publicId of 10 alphanumeric chars, retrying on collision.
 * @param {number} maxAttempts
 * @returns {Promise<string>}
 */
const generatePublicId = async (maxAttempts = 5) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        let id = '';
        for (let i = 0; i < PUBLIC_ID_LENGTH; i++) {
            id += PUBLIC_ID_CHARS.charAt(Math.floor(Math.random() * PUBLIC_ID_CHARS.length));
        }
        // Check for collision globally (publicId is unique across all orgs)
        const { data, error } = await supabase.from('Forms').select('id').eq('publicId', id).maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'generatePublicId', id, error }, 'Something went wrong generating a public form id');
            throw Error();
        }
        if (!data) return id;
    }
    ErrorHandler(Error('Failed to generate a unique public form id'), {}, 'Something went wrong generating a public form id');
    throw Error();
};

const getForms = async () => {
    const orgID = await getCurrentOrgID();

    const { error, data: forms } = await supabase
        .from('Forms')
        .select('*, FormRoles(id, roleID, canView, canEdit, canManageEntries)')
        .eq('organizationID', orgID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, error }, 'Something went wrong getting forms');
        return [];
    }

    if (!forms?.length) return [];

    // Entry counts come from the SECURITY DEFINER RPC — direct FormEntries
    // reads are restricted to admins/creators/canManageEntries members, so a
    // plain count query would silently return nothing for most users.
    const { data: counts, error: countsError } = await supabase.rpc('get_form_entry_counts');
    if (countsError) {
        ErrorHandler(Error(countsError.message), { organizationID: orgID, error: countsError }, 'Something went wrong getting form entry counts');
    }
    const countByFormID = {};
    (counts || []).forEach((row) => { countByFormID[row.formID] = Number(row.count) || 0; });

    return forms.map(f => ({
        ...f,
        entryCount: countByFormID[f.id] || 0,
    }));
};

const getForm = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('Forms')
        .select(`*,
            FormRoles(id, roleID, canView, canEdit, canManageEntries, Role:Roles(id, name)),
            CreatedBy:People!Forms_createdBy_fkey(id, firstName, lastName)
        `)
        .eq('organizationID', orgID)
        .eq('id', id)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, id, error }, 'Something went wrong getting that form');
        throw Error();
    }

    // Forms whose fill needs server-resolved context (payment setup, signature
    // content revision, resource descriptors) get it from the same
    // get-public-form endpoint the public route uses, so in-app and public
    // fills operate on identical context.
    const needsContext = data?.schema?.sections?.some((section) =>
        section.fields?.some((field) => field.type === 'payment' || field.type === 'signature'))
        || (Array.isArray(data?.resourcePaths) && data.resourcePaths.length > 0);
    if (needsContext) {
        const { data: session } = await supabase.auth.getSession();
        const result = await getPublicForm({ formID: id, accessToken: session?.session?.access_token });
        data.paymentContext = result?.form?.paymentContext;
        data.contentRevision = result?.form?.contentRevision ?? null;
        data.resources = result?.form?.resources || [];
    }
    return data;
};

const saveForm = async ({ details, originalValues }) => {
    const orgID = await getCurrentOrgID();

    const existingRoles = details.FormRoles || [];
    // Public forms: canView is always true for all assigned roles.
    // Dedupe by role id — a duplicate entry would violate FormRoles_form_role_key.
    const seenRoleIDs = new Set();
    const formRoles = (details.Roles || []).filter(r => {
        if (!r.id || seenRoleIDs.has(r.id)) return false;
        seenRoleIDs.add(r.id);
        return true;
    }).map(r => ({
        id: r.id,
        name: r.name,
        canView: details.isPublic ? true : (r.canView ?? true),
        canEdit: r.canEdit ?? false,
        canManageEntries: r.canManageEntries ?? false,
    }));

    const payload = {
        organizationID: orgID,
        title: details.title?.trim(),
        description: details.description?.trim() || null,
        submitButtonText: details.submitButtonText || 'Submit',
        successMessage: details.successMessage || 'Thanks! Your response has been recorded.',
        schema: details.schema || { version: 3, sections: [] },
        status: details.status || 'draft',
        isPublic: details.isPublic ?? false,
        slug: details.slug?.trim() || null,
        autoCreatePeople: details.autoCreatePeople ?? false,
        sendNotificationsForEntries: details.sendNotificationsForEntries ?? false,
        actions: details.actions || {},
        folderID: details.folderID || null,
        // Ordered list of storage paths for attached documents — the single
        // attachment order/truth (descriptors are derived server-side).
        resourcePaths: normalizeResourcePaths(details.resourcePaths),
        updatedAt: new Date().toISOString(),
    };

    let result;

    if (details.id) {
        // Update — bump schemaVersion if schema changed
        if (originalValues && JSON.stringify(originalValues.schema) !== JSON.stringify(payload.schema)) {
            payload.schemaVersion = (originalValues.schemaVersion || 1) + 1;
        }

        const { error, data } = await supabase.from('Forms').update(payload).eq('id', details.id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'saveForm', id: details.id, error }, 'Something went wrong saving that form');
            throw Error();
        }
        result = data;
    } else {
        payload.createdBy = details.createdBy || null;
        payload.schemaVersion = 1;
        const { error, data } = await supabase.from('Forms').insert(payload).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'saveForm', error }, 'Something went wrong creating that form');
            throw Error();
        }
        result = data;
    }

    // Generate publicId when toggling public (or creating public) if absent
    if (result.isPublic && !result.publicId) {
        const publicId = await generatePublicId();
        const { error: pidError, data: pidData } = await supabase
            .from('Forms')
            .update({ publicId })
            .eq('id', result.id)
            .select()
            .maybeSingle();
        if (pidError) {
            ErrorHandler(Error(pidError.message), { mutation: 'saveForm', id: result.id, error: pidError }, 'Something went wrong generating a public form id');
            throw Error();
        }
        result = pidData;
    }

    // Diff FormRoles: detect adds, deletes, and field-level canView/canEdit/canManageEntries updates
    const existingByRoleId = {};
    existingRoles.forEach((fr) => {
        if (fr.roleID) existingByRoleId[fr.roleID] = fr;
    });

    const newByRoleId = {};
    formRoles.forEach((r) => {
        if (r.id) newByRoleId[r.id] = r;
    });

    const rolesToAdd = formRoles.filter((r) => !existingByRoleId[r.id]);
    const rolesToDelete = existingRoles.filter((fr) => !newByRoleId[fr.roleID]);
    const rolesToUpdate = formRoles.filter((r) => {
        const existing = existingByRoleId[r.id];
        if (!existing) return false;
        return (existing.canView ?? true) !== (r.canView ?? true)
            || (existing.canEdit ?? false) !== (r.canEdit ?? false)
            || (existing.canManageEntries ?? false) !== (r.canManageEntries ?? false);
    });

    if (rolesToAdd.length > 0) {
        const { error: addError } = await supabase
            .from('FormRoles')
            // upsert: details.FormRoles can be stale vs the DB, so a role that
            // "doesn't exist" client-side may already have a row — upsert on
            // the (formID, roleID) unique key instead of failing on insert.
            .upsert(rolesToAdd.map(r => ({ organizationID: orgID, formID: result.id, roleID: r.id, canView: r.canView ?? true, canEdit: r.canEdit ?? false, canManageEntries: r.canManageEntries ?? false })), { onConflict: 'formID,roleID' });
        if (addError) {
            ErrorHandler(Error(addError.message), { mutation: 'saveForm', id: result.id, error: addError }, 'Something went wrong updating form roles');
        }
    }

    if (rolesToDelete.length > 0) {
        const { error: delError } = await supabase
            .from('FormRoles')
            .delete()
            .in('roleID', rolesToDelete.map(r => r.roleID))
            .eq('formID', result.id);
        if (delError) {
            ErrorHandler(Error(delError.message), { mutation: 'saveForm', id: result.id, error: delError }, 'Something went wrong updating form roles');
        }
    }

    if (rolesToUpdate.length > 0) {
        for (const r of rolesToUpdate) {
            const { error: updateError } = await supabase
                .from('FormRoles')
                .update({ canView: r.canView ?? true, canEdit: r.canEdit ?? false, canManageEntries: r.canManageEntries ?? false })
                .eq('formID', result.id)
                .eq('roleID', r.id);
            if (updateError) {
                ErrorHandler(Error(updateError.message), { mutation: 'saveForm', id: result.id, error: updateError }, 'Something went wrong updating form roles');
            }
        }
    }

    Toast.show({ type: 'success', text2: details.id ? 'Form updated successfully!' : 'Form created successfully!' });

    if (details.id) {
        const scalarDetails = originalValues ? buildUpdateDetails(originalValues, payload, {
            title: { label: 'Title' },
            description: { label: 'Description' },
            status: { label: 'Status' },
            isPublic: { label: 'Public' },
            slug: { label: 'Slug' },
            autoCreatePeople: { label: 'Auto-Create People' },
            sendNotificationsForEntries: { label: 'Send Notifications for Entries' },
        }) : null;
        insertActivity({ subjectType: 'Form', subjectID: result.id, subjectName: result.title, verb: 'update', details: scalarDetails }).catch(() => {});
    } else {
        insertActivity({ subjectType: 'Form', subjectID: result.id, subjectName: result.title, verb: 'create' }).catch(() => {});
    }

    return result;
};

const deleteForm = async ({ formID }) => {
    const { data: existing } = await supabase.from('Forms').select('title').eq('id', formID).maybeSingle();

    const { error } = await supabase.from('Forms').delete().eq('id', formID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteForm', formID, error }, 'Something went wrong deleting that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form has been deleted successfully!' });

    insertActivity({ subjectType: 'Form', subjectID: formID, subjectName: existing?.title, verb: 'delete' }).catch(() => {});
};

const publishForm = async (id) => {
    const { error, data } = await supabase
        .from('Forms')
        .update({ status: 'published', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'publishForm', id, error }, 'Something went wrong publishing that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form published!' });
    insertActivity({ subjectType: 'Form', subjectID: id, subjectName: data?.title, verb: 'publish' }).catch(() => {});
    return data;
};

const closeForm = async (id) => {
    const { error, data } = await supabase
        .from('Forms')
        .update({ status: 'closed', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'closeForm', id, error }, 'Something went wrong closing that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form closed.' });
    insertActivity({ subjectType: 'Form', subjectID: id, subjectName: data?.title, verb: 'close' }).catch(() => {});
    return data;
};

const archiveForm = async (id) => {
    const { error, data } = await supabase
        .from('Forms')
        .update({ status: 'archived', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'archiveForm', id, error }, 'Something went wrong archiving that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form archived.' });
    insertActivity({ subjectType: 'Form', subjectID: id, subjectName: data?.title, verb: 'archive' }).catch(() => {});
    return data;
};

// Unarchive always restores to 'draft' — never auto-republish.
const unarchiveForm = async (id) => {
    const { error, data } = await supabase
        .from('Forms')
        .update({ status: 'draft', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'unarchiveForm', id, error }, 'Something went wrong unarchiving that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form restored to draft.' });
    insertActivity({ subjectType: 'Form', subjectID: id, subjectName: data?.title, verb: 'unarchive' }).catch(() => {});
    return data;
};

const reopenForm = async (id) => {
    const { error, data } = await supabase
        .from('Forms')
        .update({ status: 'published', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'reopenForm', id, error }, 'Something went wrong reopening that form');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Form reopened!' });
    insertActivity({ subjectType: 'Form', subjectID: id, subjectName: data?.title, verb: 'reopen' }).catch(() => {});
    return data;
};

const getFormEntries = async ({ formID }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('FormEntries')
        .select(`*,
            LinkedPerson:People!FormEntries_linkedPersonID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)
        `)
        .eq('organizationID', orgID)
        .eq('formID', formID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, formID, error }, 'Something went wrong getting form entries');
        return [];
    }

    if (!data?.length) return [];

    // Fetch PathEnrollments linked to these entries via triggerContext->formEntryID
    // Filter by FORM_ENTRY trigger type to narrow the result set, then match client-side
    const { data: enrollments, error: enrollErr } = await supabase
        .from('PathEnrollments')
        .select(`id, pathID, status, triggerContext,
            Path:Paths!PathEnrollments_pathID_fkey(id, name,
                Steps:PathSteps(id, stepType)
            ),
            EnrollmentSteps:PathEnrollmentSteps(id, status, PathStep:PathSteps(id, stepType))
        `)
        .eq('organizationID', orgID)
        .eq('triggerContext->>triggerType', 'FORM_ENTRY');

    // Build a map of formEntryID -> enrollment
    const enrollmentByEntryId = {};
    if (!enrollErr && enrollments) {
        const entryIdSet = new Set(data.map((e) => e.id));
        for (const en of enrollments) {
            const formEntryID = en.triggerContext?.formEntryID;
            if (formEntryID && entryIdSet.has(formEntryID) && !enrollmentByEntryId[formEntryID]) {
                enrollmentByEntryId[formEntryID] = en;
            }
        }
    }

    return data.map((entry) => ({
        ...entry,
        PathEnrollment: enrollmentByEntryId[entry.id] || null,
    }));
};

/**
 * Collect all fields carrying signable storage paths from a form schema —
 * file answers AND signature evidence images (both in form-uploads).
 * @param {object} schema
 * @returns {Array<{fieldId, type, allowMultiple, sectionID}>}
 */
const collectFileFields = (schema) => {
    const fields = [];
    if (!schema?.sections) return fields;
    for (const section of schema.sections) {
        for (const field of section.fields || []) {
            if (field.type === 'file' || field.type === 'signature') {
                fields.push({ fieldId: field.fieldId, type: field.type, allowMultiple: field.config?.allowMultiple, sectionID: section.id });
            }
        }
    }
    return fields;
};

const resolveEntryFileUrls = async ({ payload: sourcePayload, schema, context }) => {
    const fileFields = collectFileFields(schema);
    const payload = { ...(sourcePayload || {}) };
    const sourceRepeats = sourcePayload?.__repeats__;
    if (sourceRepeats && typeof sourceRepeats === 'object') payload.__repeats__ = { ...sourceRepeats };
    if (fileFields.length === 0) return payload;

    // Normalizes a field value into the objects that carry `path`.
    // file: array|single {path}; signature: { image: { path } }.
    const filesForValue = (type, value) => {
        if (!value) return [];
        if (type === 'signature') {
            return value?.image?.path ? [value.image] : [];
        }
        return Array.isArray(value) ? value : [value];
    };
    const pathsToSign = [];
    for (const { fieldId, type, sectionID } of fileFields) {
        for (const file of filesForValue(type, payload[fieldId])) {
            if (file?.path) pathsToSign.push(file.path);
        }
        for (const instance of sourceRepeats?.[sectionID] || []) {
            for (const file of filesForValue(type, instance?.[fieldId])) {
                if (file?.path) pathsToSign.push(file.path);
            }
        }
    }
    if (pathsToSign.length === 0) return payload;

    const { data: signedUrls, error } = await supabase
        .storage
        .from('form-uploads')
        .createSignedUrls([...new Set(pathsToSign)], 60 * 60);

    if (error) {
        ErrorHandler(Error(error.message), { ...context, error }, 'Something went wrong resolving file URLs');
        return payload;
    }

    // Map signed URLs back onto the payload
    const urlByPath = {};
    for (const url of signedUrls || []) {
        if (url?.path) urlByPath[url.path] = url.signedUrl;
    }
    const addSignedUrls = (type, value) => {
        if (!value) return value;
        if (type === 'signature') {
            // Signed URL lives on the image object only — never persisted,
            // never the whole signature payload.
            return value?.image?.path
                ? { ...value, image: { ...value.image, signedUrl: urlByPath[value.image.path] || null } }
                : value;
        }
        if (Array.isArray(value)) {
            return value.map((file) => ({ ...file, signedUrl: file?.path ? urlByPath[file.path] : null }));
        }
        return value?.path ? { ...value, signedUrl: urlByPath[value.path] } : value;
    };
    for (const { fieldId, type, sectionID } of fileFields) {
        if (payload[fieldId]) payload[fieldId] = addSignedUrls(type, payload[fieldId]);
        const instances = payload.__repeats__?.[sectionID];
        if (Array.isArray(instances)) {
            payload.__repeats__[sectionID] = instances.map((instance) => instance?.[fieldId] ? {
                ...instance,
                [fieldId]: addSignedUrls(type, instance[fieldId]),
            } : instance);
        }
    }
    return payload;
};

const getFormEntryFileUrl = async ({ path, filename, inline = false }) => {
    if (!path) throw new Error('This file is unavailable.');
    // `download` forces attachment disposition; omit it entirely for inline
    // viewing (signature images render inside the review screen).
    const { data, error } = await supabase.storage.from('form-uploads').createSignedUrl(path, 5 * 60, inline ? {} : { download: filename || true });
    if (error || !data?.signedUrl) throw new Error('Unable to download this file. Check your access and try again.');
    return data.signedUrl;
};

const getFormEntry = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data: entry } = await supabase
        .from('FormEntries')
        .select(`*,
            Form:Forms(*, FormRoles(id, roleID, canView, canEdit, canManageEntries)),
            LinkedPerson:People!FormEntries_linkedPersonID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)
        `)
        .eq('organizationID', orgID)
        .eq('id', id)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, id, error }, 'Something went wrong getting that form entry');
        throw Error();
    }

    if (!entry) return null;

    const form = entry.Form;
    const isSchemaStale = form ? entry.schemaVersion !== form.schemaVersion : false;

    // Snapshot-first rendering: the schema frozen at submit time is the truth
    // for this entry; the current form schema is only a legacy fallback for
    // entries written before snapshots existed.
    const effectiveSchema = entry.schemaSnapshot || form?.schema;
    // formContentSnapshot.resources is the server-frozen descriptor list
    // ({ path, filename }) accepted at submit time — NOT `resourcePaths`.
    // Derive the ordered path list from it; fall back to the live form.
    const snapshotResources = Array.isArray(entry.formContentSnapshot?.resources)
        ? entry.formContentSnapshot.resources.filter((d) => d?.path)
        : null;
    const effectiveForm = form ? {
        ...form,
        schema: effectiveSchema,
        resourcePaths: snapshotResources ? snapshotResources.map((d) => d.path) : form.resourcePaths,
        resources: snapshotResources,
        title: entry.formContentSnapshot?.title || form.title,
        description: entry.formContentSnapshot?.description ?? form.description,
    } : form;

    // Resolve signed URLs for file + signature fields (from form-uploads bucket)
    const payload = await resolveEntryFileUrls({
        payload: entry.payload,
        schema: effectiveSchema,
        context: { mutation: 'getFormEntry', id },
    });

    return { ...entry, payload, isSchemaStale, Form: effectiveForm };
};

const updateEntryLink = async ({ entryID, personID }) => {
    const { error, data } = await supabase
        .from('FormEntries')
        .update({ linkedPersonID: personID, linkAmbiguous: false })
        .eq('id', entryID)
        .select()
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'updateEntryLink', entryID, personID, error }, 'Something went wrong updating that entry');
        throw Error();
    }

    Toast.show({ type: 'success', text2: personID ? 'Entry linked successfully!' : 'Entry unlinked.' });
    return data;
};

/**
 * Find the first Name field value in an entry payload given a form schema.
 * @param {object} schema
 * @param {object} payload
 * @returns {{first, last}|null}
 */
const findNameValue = (schema, payload) => {
    if (!schema?.sections) return null;
    for (const section of schema.sections) {
        for (const field of section.fields || []) {
            if (field.type === 'name' && payload[field.fieldId]) {
                return payload[field.fieldId];
            }
        }
    }
    return null;
};

/**
 * Find the first field value of a given type in an entry payload.
 * @param {object} schema
 * @param {object} payload
 * @param {string} type
 * @returns {*|null}
 */
const findFieldValueByType = (schema, payload, type) => {
    if (!schema?.sections) return null;
    for (const section of schema.sections) {
        for (const field of section.fields || []) {
            if (field.type === type && payload[field.fieldId] != null) {
                return payload[field.fieldId];
            }
        }
    }
    return null;
};

const convertEntryToPerson = async ({ entryID }) => {
    const orgID = await getCurrentOrgID();

    // Fetch the entry + parent form
    const { error: entryError, data: entry } = await supabase
        .from('FormEntries')
        .select(`*, Form:Forms(*)`)
        .eq('organizationID', orgID)
        .eq('id', entryID)
        .maybeSingle();

    if (entryError) {
        ErrorHandler(Error(entryError.message), { mutation: 'convertEntryToPerson', entryID, error: entryError }, 'Something went wrong fetching that entry');
        throw Error();
    }
    if (!entry) {
        Toast.show({ type: 'error', text2: 'Entry not found' });
        throw Error('Entry not found');
    }

    const schema = entry.Form?.schema;
    const payload = entry.payload || {};
    const nameValue = findNameValue(schema, payload);

    if (!nameValue || (!nameValue.first && !nameValue.last)) {
        Toast.show({ type: 'error', text2: 'No name field found in this entry' });
        throw Error('No name field found');
    }

    // Create People row
    const person = {
        organizationID: orgID,
        firstName: nameValue.first?.trim() || null,
        lastName: nameValue.last?.trim() || null,
    };

    const { error: personError, data: newPerson } = await supabase.from('People').insert(person).select(PEOPLE_SELECT).single();
    if (personError) {
        ErrorHandler(Error(personError.message), { mutation: 'convertEntryToPerson', input: person, error: personError }, 'Something went wrong creating that person');
        throw Error();
    }

    const errors = [];

    // Create Addresses row if an address field exists
    const addressValue = findFieldValueByType(schema, payload, 'address');
    if (addressValue && (addressValue.street1 || addressValue.city || addressValue.state || addressValue.zip)) {
        const { error: addressError } = await supabase.from('Addresses').insert({
            organizationID: orgID,
            personID: newPerson.id,
            street: addressValue.street1?.trim() || null,
            streetLine2: addressValue.street2?.trim() || null,
            city: addressValue.city?.trim() || null,
            state: addressValue.state?.trim() || null,
            zip: addressValue.zip?.trim() || null,
        });
        if (addressError) errors.push(addressError);
    }

    // Create PeoplePhones row if a phone field exists
    const phoneValue = findFieldValueByType(schema, payload, 'phone');
    if (phoneValue) {
        const normalized = String(phoneValue).replace(/\D/g, '');
        if (normalized) {
            const { error: phoneError } = await supabase.from('PeoplePhones').insert({
                organizationID: orgID,
                personID: newPerson.id,
                phoneNumber: normalized,
                label: null,
                smsOptInStatus: 'never_opted_in',
                private: false,
            });
            if (phoneError) errors.push(phoneError);
        }
    }

    if (errors.length > 0) {
        ErrorHandler(Error('One or more errors encountered creating a person from entry'), { mutation: 'convertEntryToPerson', errors }, 'We created the person, but something went wrong creating related records');
    }

    // Link the entry to the new person
    const { error: linkError } = await supabase
        .from('FormEntries')
        .update({ linkedPersonID: newPerson.id, linkAmbiguous: false })
        .eq('id', entryID);

    if (linkError) {
        ErrorHandler(Error(linkError.message), { mutation: 'convertEntryToPerson', entryID, error: linkError }, 'Something went wrong linking the entry to the new person');
        throw Error();
    }

    Toast.show({ type: 'success', text2: `${newPerson.firstName || ''} ${newPerson.lastName || ''} has been created and linked!` });

    insertActivity({
        subjectType: 'Person',
        subjectID: newPerson.id,
        subjectName: `${newPerson.firstName ?? ''} ${newPerson.lastName ?? ''}`.trim(),
        verb: 'create',
    }).catch(() => {});

    return newPerson;
};

/**
 * Flatten a single field value into one or more CSV columns.
 * Composite fields expand to sub-columns (address.street1, name.first, etc.).
 * @param {object} field
 * @param {*} value
 * @returns {object} map of column -> value
 */
const flattenFieldValue = (field, value) => {
    const columns = {};
    if (value == null) return columns;

    switch (field.type) {
        case 'address': {
            const sub = ['street1', 'street2', 'city', 'state', 'zip'];
            for (const key of sub) {
                columns[`${field.fieldId}.${key}`] = value[key] ?? '';
            }
            break;
        }
        case 'name': {
            columns[`${field.fieldId}.first`] = value.first ?? '';
            columns[`${field.fieldId}.last`] = value.last ?? '';
            if (value.linkedPersonId) columns[`${field.fieldId}.linkedPersonId`] = value.linkedPersonId;
            break;
        }
        case 'file': {
            if (Array.isArray(value)) {
                columns[field.fieldId] = value.map(f => f?.filename || f?.path || '').join('; ');
            } else {
                columns[field.fieldId] = value?.filename || value?.path || '';
            }
            break;
        }
        case 'checkboxGroup': {
            columns[field.fieldId] = Array.isArray(value) ? value.join('; ') : (value ?? '');
            break;
        }
        case 'signature': {
            // Evidence metadata only — never the image bytes, base64, storage
            // path, or signed URLs.
            columns[`${field.fieldId}.signerName`] = value.signerName ?? '';
            columns[`${field.fieldId}.signerRole`] = value.signerRole ?? '';
            columns[`${field.fieldId}.participantName`] = value.participantName ?? '';
            columns[`${field.fieldId}.signedAt`] = value.signedAt ?? '';
            columns[`${field.fieldId}.consentAccepted`] = value.consentAccepted ?? '';
            columns[`${field.fieldId}.consentVersion`] = value.consentVersion ?? '';
            break;
        }
        case 'profile': {
            // Person id only — the full profile-card JSON stays out of exports.
            columns[field.fieldId] = value?.personID ?? '';
            break;
        }
        default:
            columns[field.fieldId] = typeof value === 'object' ? JSON.stringify(value) : (value ?? '');
    }
    return columns;
};

/**
 * Build an ordered list of columns from a form schema, expanding composite fields.
 * @param {object} schema
 * @returns {Array<string>}
 */
const buildExportColumns = (schema) => {
    const columns = ['createdAt', 'submittedByPersonID', 'linkedPersonID', 'linkAmbiguous'];
    if (!schema?.sections) return columns;
    for (const section of schema.sections) {
        for (const field of section.fields || []) {
            if (field.type === 'address') {
                columns.push(`${field.fieldId}.street1`, `${field.fieldId}.street2`, `${field.fieldId}.city`, `${field.fieldId}.state`, `${field.fieldId}.zip`);
            } else if (field.type === 'name') {
                columns.push(`${field.fieldId}.first`, `${field.fieldId}.last`);
            } else if (field.type === 'signature') {
                columns.push(
                    `${field.fieldId}.signerName`, `${field.fieldId}.signerRole`,
                    `${field.fieldId}.participantName`, `${field.fieldId}.signedAt`,
                    `${field.fieldId}.consentAccepted`, `${field.fieldId}.consentVersion`,
                );
            } else {
                columns.push(field.fieldId);
            }
        }
    }
    return columns;
};

/** Map of fieldId -> field for a schema (snapshot or current). */
const fieldsByFieldIdFor = (schema) => {
    const map = {};
    for (const section of schema?.sections || []) {
        for (const field of section.fields || []) {
            map[field.fieldId] = field;
        }
    }
    return map;
};

const exportEntriesCSV = async ({ formID }) => {
    const orgID = await getCurrentOrgID();

    const { error: formError, data: form } = await supabase
        .from('Forms')
        .select('id, title, schema')
        .eq('organizationID', orgID)
        .eq('id', formID)
        .maybeSingle();

    if (formError) {
        ErrorHandler(Error(formError.message), { mutation: 'exportEntriesCSV', formID, error: formError }, 'Something went wrong fetching that form');
        throw Error();
    }
    if (!form) {
        Toast.show({ type: 'error', text2: 'Form not found' });
        throw Error('Form not found');
    }

    const { error: entriesError, data: entries } = await supabase
        .from('FormEntries')
        .select('*')
        .eq('organizationID', orgID)
        .eq('formID', formID)
        .order('createdAt', { ascending: false });

    if (entriesError) {
        ErrorHandler(Error(entriesError.message), { mutation: 'exportEntriesCSV', formID, error: entriesError }, 'Something went wrong fetching form entries');
        throw Error();
    }

    if (!entries?.length) {
        Toast.show({ type: 'error', text2: 'No entries to export' });
        return false;
    }

    const schema = form.schema;
    const columns = buildExportColumns(schema);

    // Union mixed entry snapshots: an entry submitted under an older schema
    // can carry fields the current schema no longer has. Snapshot columns are
    // appended after current-schema columns; header/value parity is preserved
    // because rows are projected through `columns` below.
    const baseColumnCount = 4; // createdAt, submittedByPersonID, linkedPersonID, linkAmbiguous
    const seenColumns = new Set(columns);
    for (const entry of entries) {
        if (!entry.schemaSnapshot) continue;
        for (const col of buildExportColumns(entry.schemaSnapshot).slice(baseColumnCount)) {
            if (!seenColumns.has(col)) {
                seenColumns.add(col);
                columns.push(col);
            }
        }
    }
    if (entries.some((entry) => entry.paymentSnapshot)) columns.push('paymentStatus', 'paymentAmount', 'paymentOption', 'paymentFund', 'archivedAt');

    // Build a lookup of fields by fieldId for flattening
    const fieldsByFieldId = fieldsByFieldIdFor(schema);

    const rows = entries.map(entry => {
        // Entry snapshots take precedence over the current schema for that
        // entry's payload — a removed/changed field still flattens correctly.
        const entryFieldsById = entry.schemaSnapshot
            ? { ...fieldsByFieldId, ...fieldsByFieldIdFor(entry.schemaSnapshot) }
            : fieldsByFieldId;
        const row = {
            createdAt: entry.createdAt,
            submittedByPersonID: entry.submittedByPersonID || '',
            linkedPersonID: entry.linkedPersonID || '',
            linkAmbiguous: entry.linkAmbiguous ?? false,
            paymentStatus: entry.paymentStatus || '',
            paymentAmount: entry.paymentSnapshot?.amountCents != null ? (entry.paymentSnapshot.amountCents / 100).toFixed(2) : '',
            paymentOption: entry.paymentSnapshot?.optionLabel || '',
            paymentFund: entry.paymentSnapshot?.fundName || '',
            archivedAt: entry.archivedAt || '',
        };
        const payload = entry.payload || {};
        for (const fieldId of Object.keys(payload)) {
            const field = entryFieldsById[fieldId];
            if (!field) {
                row[fieldId] = typeof payload[fieldId] === 'object' ? JSON.stringify(payload[fieldId]) : (payload[fieldId] ?? '');
                continue;
            }
            const flattened = flattenFieldValue(field, payload[fieldId]);
            Object.assign(row, flattened);
        }
        return row;
    });

    // Ensure every row has all columns (fill missing with '')
    const exportData = rows.map(row => {
        const full = {};
        for (const col of columns) {
            full[col] = row[col] ?? '';
        }
        return full;
    });

    const headers = {};
    for (const col of columns) {
        headers[col] = col;
    }

    const filename = `${form.title?.replace(/[^a-zA-Z0-9-_]/g, '_') || 'form'}_entries`;
    return await DataExporter.exportToCSV(exportData, filename, { headers });
};

/**
 * Thin wrapper over the submit-form edge function.
 * @param {object} body - { formId?, publicId?, payload, files?, accessToken?, captcha_token?, website?, form_loaded_at? }
 * @returns {Promise<object>} response data
 */
const submitForm = async (body) => {
    const { data, error } = await supabase.functions.invoke('submit-form', { body });

    if (error) {
        let errorDetail = error.message;
        let errorBody = null;
        try {
            if (error.context && typeof error.context.json === 'function') {
                errorBody = await error.context.json();
                errorDetail = errorBody?.error || (errorBody?.errors ? Object.values(errorBody.errors)[0] : null) || errorDetail;
            }
        } catch { /* response body not JSON, use default */ }
        // 4xx are expected user-facing errors (form closed, validation failures,
        // forbidden, etc.) — don't report to Sentry. 5xx are real failures and
        // are also captured server-side. See CHURCH-APP-13P.
        const status = error.context?.status;
        const err = new Error(errorDetail);
        err.status = status;
        // Structured per-field validation errors — keyed by fieldId or repeat
        // instance key — map back onto the form in FormView.
        if (errorBody?.errors && typeof errorBody.errors === 'object') err.fieldErrors = errorBody.errors;
        if (status >= 400 && status < 500) {
            throw err;
        }
        ErrorHandler(Error(errorDetail), { mutation: 'submitForm', error: errorDetail, rawError: error.message }, 'Something went wrong submitting that form');
        throw err;
    }

    return data;
};

/**
 * Wrapper over the get-public-form edge function.
 * @param {object} params - { publicId, accessToken?, formID? }
 *   - publicId: opaque public id (public path). Always sent for public forms.
 *   - formID + accessToken: form UUID + JWT (role-scoped in-app path, when NOT using publicId).
 * @returns {Promise<object>} response data
 */
const getPublicForm = async ({ publicId, accessToken, formID }) => {
    // Public path: always send publicId. Include accessToken when present so the
    // renderer can do Name autofill (the backend public path ignores it, but the
    // caller uses the session separately).
    // Role-scoped in-app path (no publicId): send formID + accessToken.
    const body = publicId
        ? { publicId, ...(accessToken ? { accessToken } : {}) }
        : { formID, accessToken };

    // Retry transient network failures (Safari "Load failed", Supabase
    // FunctionsFetchError "Failed to send a request to the Edge Function").
    // getPublicForm is called directly (not via React Query), so the global
    // shouldRetryQuery logic doesn't apply. See CHURCH-APP-13M.
    const maxRetries = 3;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const { data, error } = await supabase.functions.invoke('get-public-form', { body });

        if (!error) return data;

        const status = error.context?.status;

        // 404/403 are expected user-facing states (form closed/deleted/forbidden),
        // not bugs — don't report or retry. The caller ([publicId].jsx) renders the
        // appropriate status.
        if (status === 404) throw Error('not_found');
        if (status === 403) throw Error('forbidden');

        // 5xx are real server failures — captured server-side by the edge fn's own
        // Sentry init (reach-edge-functions). Don't retry (server is down/erroring)
        // and don't re-report client-side. See CHURCH-APP-17A.
        if (status >= 500) {
            throw Error('server_error');
        }

        // Network/load failures (no status, FunctionsFetchError): retry with backoff.
        const isNetworkError = error.name === 'FunctionsFetchError'
            || /Load failed|Failed to send a request to the Edge Function|Network|fetch/i.test(error.message);
        if (isNetworkError && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
            continue;
        }

        // Non-retryable or retries exhausted — report unexpected errors to Sentry.
        ErrorHandler(Error(error.message), { mutation: 'getPublicForm', publicId, formID, error }, 'Something went wrong fetching that public form');
        throw Error();
    }

    // All retries exhausted on a network error — surface a network status to the
    // caller instead of a misleading 'not_found'.
    throw Error('network');
};

const getFormsTriggeringPath = async ({ pathID }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('Forms')
        .select('id, title, status, schema')
        .eq('organizationID', orgID)
        .eq('actions->triggerPath->>enabled', 'true')
        .eq('actions->triggerPath->>pathId', pathID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, pathID, error }, 'Something went wrong getting forms triggering this path');
        return [];
    }

    return data || [];
};

const getFormFolders = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('FormFolders')
        .select('*')
        .eq('organizationID', orgID)
        .order('name', { ascending: true });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getFormFolders', organizationID: orgID, error }, 'Something went wrong getting form folders');
        return [];
    }
    return data || [];
};

const saveFormFolder = async ({ id, name, parentFolderID = null }) => {
    const orgID = await getCurrentOrgID();
    const payload = { name: name.trim(), parentFolderID: parentFolderID || null, updatedAt: new Date().toISOString() };
    const query = id
        ? supabase.from('FormFolders').update(payload).eq('id', id)
        : supabase.from('FormFolders').insert({ ...payload, organizationID: orgID });
    const { data, error } = await query.select().single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'saveFormFolder', id, error }, 'Something went wrong saving that form folder');
        throw Error();
    }
    Toast.show({ type: 'success', text2: id ? 'Folder renamed.' : 'Folder created.' });
    return data;
};

const deleteFormFolder = async ({ folderID }) => {
    const { error } = await supabase.from('FormFolders').delete().eq('id', folderID);
    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteFormFolder', folderID, error }, 'Something went wrong deleting that form folder');
        throw Error();
    }
    Toast.show({ type: 'success', text2: 'Folder deleted.' });
};

const moveFormToFolder = async ({ formID, folderID }) => {
    const { data, error } = await supabase
        .from('Forms')
        .update({ folderID: folderID || null, updatedAt: new Date().toISOString() })
        .eq('id', formID)
        .select()
        .single();
    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'moveFormToFolder', formID, folderID, error }, 'Something went wrong moving that form');
        throw Error();
    }
    Toast.show({ type: 'success', text2: folderID ? 'Form moved.' : 'Form moved to uncategorized.' });
    return data;
};

const getMyFormEntries = async ({ entryID = null, limit = null } = {}) => {
    const { data, error } = await supabase.rpc('get_my_form_entries', {
        p_entry_id: entryID,
        p_limit: limit,
    });
    if (error) {
        ErrorHandler(Error(error.message), { query: 'getMyFormEntries', entryID, limit, error }, 'Something went wrong getting your form entries');
        return [];
    }
    const entries = (data || []).map((entry) => ({
        ...entry,
        payload: { ...(entry.payload || {}) },
        Form: {
            id: entry.formID,
            title: entry.formTitle,
            schema: entry.formSchema,
            schemaVersion: entry.formSchemaVersion,
        },
        isSchemaStale: entry.formSchemaVersion != null && entry.schemaVersion !== entry.formSchemaVersion,
    }));
    if (!entryID) return entries;

    return Promise.all(entries.map(async (entry) => ({
        ...entry,
        payload: await resolveEntryFileUrls({
            payload: entry.payload,
            schema: entry.formSchema,
            context: { query: 'getMyFormEntries', entryID: entry.id },
        }),
    })));
};

const hasEditableForms = async () => {
    const { data, error } = await supabase.rpc('has_editable_forms');
    if (error) throw new Error(error.message);
    return data === true;
};

const deleteFormEntry = async ({ entryID }) => {
    const { data: entry, error: readError } = await supabase.from('FormEntries').select('paymentSnapshot').eq('id', entryID).single();
    if (readError) throw new Error(readError.message);
    const { error } = entry.paymentSnapshot
        ? await supabase.from('FormEntries').update({ archivedAt: new Date().toISOString() }).eq('id', entryID)
        : await supabase.from('FormEntries').delete().eq('id', entryID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteFormEntry', entryID, error }, 'Something went wrong deleting that entry');
        throw Error();
    }

    Toast.show({ type: 'success', text2: entry.paymentSnapshot ? 'Entry archived. Payment history preserved.' : 'Entry deleted.' });
};

export {
    getForms,
    getForm,
    saveForm,
    deleteForm,
    publishForm,
    closeForm,
    archiveForm,
    unarchiveForm,
    reopenForm,
    getFormEntries,
    getFormEntry,
    getFormEntryFileUrl,
    updateEntryLink,
    deleteFormEntry,
    convertEntryToPerson,
    exportEntriesCSV,
    submitForm,
    getPublicForm,
    getFormsTriggeringPath,
    getFormFolders,
    saveFormFolder,
    deleteFormFolder,
    moveFormToFolder,
    getMyFormEntries,
    hasEditableForms,
};
